import { JSXElementConstructor, ReactElement, useMemo, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';

import { AdaptiveFormConfig } from '@elements';
import { useProposalStore } from '@store/proposal';
import { HealthInsuredMemberBasic, InsuredMemberBase } from '@store/proposal/types';
import { dayjs, Stack, Text } from '@ui';
import {
  DATE_FORMAT,
  DAUGHTER,
  FEMALE,
  MALE,
  MOTHER,
  MOTHER_IN_LAW,
  SELF,
  SPOUSE
} from '@utils/constants';

import { LazyUnmountWrapper } from './LazyUnmountWrapper';
import { MemberAccordionItem } from './MemberAccordionItem';

export type MembersAccordionProps = {
  name: string;
  label: string | ReactElement<any, string | JSXElementConstructor<any>>;
  subtitle?: string;
  onSelectKey: string;
  questionError?: string;
  getSubQuestionsConfig: (mIndex: number) => AdaptiveFormConfig;
  memberIdsWithDisease?: string[];
  pregnancyQuestion?: boolean;
  showMemberRadios?: boolean;
  lazyUnmount?: boolean;
};

// todo : check the commented code is relevant, keep it or remove it after all forms are migrated to adaptive form (base is RHFQuestionsForMembers)
export const MembersAccordion = ({
  name,
  label,
  // subtitle,
  // questionError,
  onSelectKey,
  getSubQuestionsConfig,
  memberIdsWithDisease,
  pregnancyQuestion = false,
  showMemberRadios = false,
  lazyUnmount = false
}: MembersAccordionProps) => {
  /*
    insured_basic should satisfy the following shape:
    insured_basic: {member_id: string, full_name: string, relation: string}[]

    The indices of the members in insured_basic should match the indices of the members in insured_medical.
  */

  const { control, formState, clearErrors, setValue } = useFormContext();

  const proposalForm = useProposalStore((state) => state.proposalForm);
  const insuredMembers: HealthInsuredMemberBasic[] = proposalForm?.insured_basic;
  const proposerGender = proposalForm?.proposer?.gender;

  const checkIsFemaleAdult = useCallback(
    (member: InsuredMemberBase) => {
      if ([DAUGHTER, MOTHER, MOTHER_IN_LAW].includes(member.relation)) {
        return true;
      }

      if (proposerGender === MALE && member.relation === SPOUSE) {
        return true;
      }

      if (proposerGender === FEMALE && member.relation === SELF) {
        return true;
      }

      return false;
    },
    [proposerGender]
  );

  // Memoize eligible members calculation
  const eligibleMembers = useMemo(() => {
    if (!pregnancyQuestion) return insuredMembers;

    return insuredMembers?.filter((member) => {
      const isAdult = dayjs().diff(dayjs(member.dob, DATE_FORMAT), 'years') >= 18;
      if (!isAdult) return false;

      const isFemaleAdult = checkIsFemaleAdult(member);
      if (!isFemaleAdult) return false;

      return true;
    });
  }, [pregnancyQuestion, insuredMembers, checkIsFemaleAdult]);

  // Memoize eligible member mapping
  const eligibleMemberIdMapping = useMemo(() => {
    return (
      eligibleMembers?.reduce((mapping, member) => {
        if (member.member_id) return { ...mapping, [member.member_id]: member };
        return mapping;
      }, {} as Record<string, InsuredMemberBase>) || {}
    );
  }, [eligibleMembers]);

  return insuredMembers?.length ? (
    <Stack w="100%" gap={0}>
      {label && (
        <Text mb="xs" c="secondary">
          {label}
        </Text>
      )}
      {/**
       * NOTE: Iterating over only eligibleMembers will result in wrong memberIndex being mapped for field name
       * ex: If spouse of index 1 in insuredMembers list and for pregnancy question spouse will be index 0 in eligibleMembers list
       * Thus setting insured_medical[0].pregnant true instead of insured_medical[1].pregnant
       * So, we should always iterate over all members but return null for non eligible members to avoid interaction.
       * Nothing in UI indicates this as it uses index to check selected items (displays index 0 of eligibleMembers but data saves at index 0 of insuredMembers), data will be saved incorrectly.
       * */}
      {insuredMembers.map((member, memberIndex) => {
        if (!eligibleMemberIdMapping[member.member_id!]) return null;

        const subQuestionsConfig = getSubQuestionsConfig(memberIndex);

        const questionKey = onSelectKey.replace('.answer', '');
        const errors = formState.errors?.[name] as Record<number, any> | undefined;
        const hasError = errors?.[memberIndex]?.[questionKey];

        return (
          <LazyUnmountWrapper key={memberIndex} enabled={lazyUnmount && !hasError}>
            <MemberAccordionItem
              member={member}
              memberIndex={memberIndex}
              name={name}
              onSelectKey={onSelectKey}
              control={control}
              setValue={setValue}
              clearErrors={clearErrors}
              subQuestionsConfig={subQuestionsConfig}
              hasError={!!hasError}
              memberIdsWithDisease={memberIdsWithDisease}
              showMemberRadios={showMemberRadios}
            />
          </LazyUnmountWrapper>
        );
      })}
    </Stack>
  ) : null;
};
